import React, { useEffect, useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { adminCorreiosGet, adminCorreiosSave, adminCorreiosTestar, adminCorreiosChaveGet, adminCorreiosChaveSave, adminCorreiosTestarEtiqueta } from '../../lib/api';

const empty = { usuario: '', senha: '', cartao: '', contrato: '', cod_admin: '', ambiente: 'homologacao' };

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-sm font-medium text-slate-700">{label}</span>
    {children}
    {hint && <span className="block text-xs text-slate-500 mt-1">{hint}</span>}
  </label>
);

const CorreiosSection = () => {
  const [form, setForm] = useState(empty);
  const [temSenha, setTemSenha] = useState(false);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [chaveInfo, setChaveInfo] = useState(null);
  const [chave, setChave] = useState('');
  const [chaveExpira, setChaveExpira] = useState('');
  const [msgChave, setMsgChave] = useState(null);
  const [testandoEtiqueta, setTestandoEtiqueta] = useState(false);

  useEffect(() => {
    adminCorreiosGet().then((c) => {
      setForm({ ...empty, usuario: c.usuario, cartao: c.cartao, contrato: c.contrato, cod_admin: c.cod_admin, ambiente: c.ambiente });
      setTemSenha(c.tem_senha);
    });
  }, []);

  useEffect(() => { adminCorreiosChaveGet().then(setChaveInfo).catch(() => setChaveInfo({ configurada: false })); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const salvarChave = async (valor) => {
    setBusy(true); setMsgChave(null);
    try {
      setChaveInfo(await adminCorreiosChaveSave(valor, valor ? chaveExpira : null));
      setChave(''); if (!valor) setChaveExpira('');
      setMsgChave({ ok: true, text: valor ? 'Chave salva. O sistema já passou a usá-la.' : 'Chave removida. Voltou a usar usuário e código de acesso.' });
    } catch (err) {
      setMsgChave({ ok: false, text: err?.response?.data?.detail || 'Não foi possível salvar a chave.' });
    } finally { setBusy(false); }
  };

  const testarEtiqueta = async () => {
    setTestandoEtiqueta(true); setMsgChave(null);
    try { const r = await adminCorreiosTestarEtiqueta(); setMsgChave({ ok: r.ok, text: r.mensagem }); }
    catch { setMsgChave({ ok: false, text: 'Não foi possível fazer o teste agora.' }); }
    finally { setTestandoEtiqueta(false); }
  };

  const salvar = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      const c = await adminCorreiosSave(form);
      setTemSenha(c.tem_senha);
      setForm({ ...form, senha: '' });
      setMsg({ ok: true, text: 'Configuração salva.' });
    } catch (err) {
      setMsg({ ok: false, text: err?.response?.data?.detail || 'Erro ao salvar.' });
    } finally { setBusy(false); }
  };

  const testar = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await adminCorreiosTestar();
      setMsg({ ok: r.ok, text: r.mensagem });
    } catch { setMsg({ ok: false, text: 'Erro ao testar a conexão.' }); }
    finally { setBusy(false); }
  };

  return (
    <section className="bg-white rounded-2xl border border-slate-100 p-6 max-w-2xl">
      <h2 className="text-lg font-semibold text-slate-900">Integração API Correios</h2>
      <p className="text-sm text-slate-500 mb-5">Credenciais do seu contrato (CWS)</p>
      <form onSubmit={salvar} className="space-y-5">
        <Field label="Ambiente">
          <select value={form.ambiente} onChange={set('ambiente')} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm">
            <option value="homologacao">Homologação (testes)</option>
            <option value="producao">Produção</option>
          </select>
        </Field>
        <Field label="Usuário (CWS)"><Input className="mt-1" value={form.usuario} onChange={set('usuario')} required /></Field>
        <Field label="Código de acesso à API" hint={temSenha ? 'Já salvo. Deixe em branco para manter o atual.' : 'Gerado no portal CWS. Não é a senha do site.'}>
          <Input className="mt-1" type="password" autoComplete="new-password" value={form.senha} onChange={set('senha')} required={!temSenha} />
        </Field>
        <Field label="Cartão de postagem"><Input className="mt-1" value={form.cartao} onChange={set('cartao')} required /></Field>
        <Field label="Número do contrato"><Input className="mt-1" value={form.contrato} onChange={set('contrato')} /></Field>
        <Field label="Código administrativo (DR)" hint="Opcional."><Input className="mt-1" value={form.cod_admin} onChange={set('cod_admin')} /></Field>
        {msg && <p className={`text-sm ${msg.ok ? 'text-emerald-600' : 'text-red-600'}`}>{msg.text}</p>}
        <div className="flex gap-3">
          <Button type="submit" disabled={busy} className="inn-btn-primary rounded-full h-10 px-6">Salvar</Button>
          <Button type="button" variant="outline" disabled={busy} onClick={testar} className="rounded-full h-10 px-6">Testar conexão</Button>
        </div>
      </form>

      <div className="mt-8 border-t border-slate-100 pt-6">
        <h3 className="text-base font-semibold text-slate-900">Chave de acesso (subdelegação)</h3>
        <p className="text-sm text-slate-500 mt-1">
          Chave criada no portal dos Correios, por cartão de postagem, que vale <strong>só para as etiquetas</strong> (API de Pré-postagem). Preço, prazo e rastreio continuam pelo usuário e código de acesso acima.
        </p>
        {chaveInfo && (
          <p className={`text-sm font-medium mt-3 ${chaveInfo.configurada ? 'text-emerald-600' : 'text-amber-600'}`}>
            {chaveInfo.configurada
              ? `Em uso — chave terminada em ${chaveInfo.final}${chaveInfo.expira_em ? `, vale até ${chaveInfo.expira_em.split('-').reverse().join('/')} (${chaveInfo.dias_restantes} dias${chaveInfo.dias_restantes <= 15 ? ' — CRIE UMA NOVA CHAVE LOGO' : ''})` : chaveInfo.salva_em ? `, salva em ${chaveInfo.salva_em.split('-').reverse().join('/')} (vence em até 180 dias; informe a validade abaixo para eu avisar)` : ''}.`
              : 'Nenhuma chave salva: as etiquetas também usam o usuário e o código de acesso acima.'}
          </p>
        )}
        <label className="block mt-4">
          <span className="text-sm font-medium text-slate-700">Colar a chave</span>
          <textarea value={chave} onChange={(e) => setChave(e.target.value)} rows={3} placeholder="Cole aqui a chave (começa com cws-ch1_) gerada em cws.correios.com.br → Chaves de Acesso" className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-xs font-mono" />
        </label>
        <label className="block mt-3">
          <span className="text-sm font-medium text-slate-700">Validade da chave (opcional)</span>
          <input type="date" value={chaveExpira} onChange={(e) => setChaveExpira(e.target.value)} className="mt-1 h-10 rounded-md border border-slate-200 px-3 text-sm block" />
          <span className="block text-xs text-slate-500 mt-1">Data de expiração que você escolheu no portal. Serve só para a tela te avisar antes de vencer.</span>
        </label>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <Button type="button" disabled={busy || !chave.trim()} onClick={() => salvarChave(chave)} className="inn-btn-primary rounded-full h-10 px-6">Salvar chave</Button>
          {chaveInfo?.configurada && <button type="button" onClick={() => salvarChave('')} className="text-sm text-red-600 hover:underline">Remover chave</button>}
          <Button type="button" variant="outline" disabled={testandoEtiqueta} onClick={testarEtiqueta} className="rounded-full h-10 px-6">
            {testandoEtiqueta ? 'Testando (uns 10 s)...' : 'Testar emissão de etiqueta'}
          </Button>
        </div>
        {msgChave && <p className={`mt-3 text-sm rounded-lg p-3 ${msgChave.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'}`}>{msgChave.text}</p>}
        <div className="mt-5 text-sm text-slate-600 bg-slate-50 rounded-xl p-4 space-y-2">
          <p className="font-semibold text-slate-800">Como criar a chave</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Entre em <a href="https://cws.correios.com.br/chaves-acesso" target="_blank" rel="noreferrer" className="text-[#2A5BC7] underline">cws.correios.com.br/chaves-acesso</a> com o login do Meu Correios da empresa.</li>
            <li>Clique em <strong>Criar nova Chave</strong>, dê um nome (ex.: In&apos;Nova Envios) e escolha a expiração (no máximo 180 dias).</li>
            <li>Em APIs, use <strong>restritas por cartão de postagem</strong> e digite o número do cartão. Ligue só <strong>Pré-postagem (36)</strong> com <strong>Acesso total</strong>; as outras opções do cartão não são necessárias.</li>
            <li>Copie a chave, cole acima e clique em <strong>Salvar chave</strong>. Depois use <strong>Testar emissão de etiqueta</strong> para ver se os Correios voltaram a aceitar.</li>
          </ol>
          <p className="text-xs text-slate-500">A chave vence em até 180 dias e não renova: antes do vencimento é preciso criar outra e colar aqui.</p>
        </div>
      </div>
    </section>
  );
};

export default CorreiosSection;
